import type { Connection, IntegrationCatalogItem, UiStatus } from "../api/types"

/**
 * Single source of truth for the status displayed on a card / detail page.
 * Combines tenant-agnostic catalog availability, per-tenant pricing flags
 * and per-tenant connection health.
 */
export function deriveStatus(item: IntegrationCatalogItem, connection?: Connection | null): UiStatus {
  if (item.availability === "coming_soon") return "coming_soon"
  if (item.pricing_locked) return "pricing_locked"
  if (!connection) return "available"
  return connection.health
}

/** Maps UiStatus → Badge variant from design system. */
export type BadgeVariant = "green" | "warning" | "destructive" | "secondary" | "frozen"

type StatusMeta = {
  labelKey: string
  labelFallback: string
  badgeVariant: BadgeVariant
  /** Tailwind class for the colored dot indicator. */
  dotClass: string
}

const META: Record<UiStatus, StatusMeta> = {
  connected: {
    labelKey: "integrations_status_connected",
    labelFallback: "Connected",
    badgeVariant: "green",
    dotClass: "bg-success-500",
  },
  warning: {
    labelKey: "integrations_status_warning",
    labelFallback: "Warning",
    badgeVariant: "warning",
    dotClass: "bg-warning-500",
  },
  error: {
    labelKey: "integrations_status_error",
    labelFallback: "Error",
    badgeVariant: "destructive",
    dotClass: "bg-error-500",
  },
  available: {
    labelKey: "integrations_status_available",
    labelFallback: "Available",
    badgeVariant: "secondary",
    dotClass: "bg-neutral-400",
  },
  coming_soon: {
    labelKey: "integrations_status_coming_soon",
    labelFallback: "Coming soon",
    badgeVariant: "secondary",
    dotClass: "bg-brand-500",
  },
  pricing_locked: {
    labelKey: "integrations_status_available",
    labelFallback: "Available",
    badgeVariant: "frozen",
    dotClass: "bg-neutral-300",
  },
}

export function statusMeta(status: UiStatus): StatusMeta {
  return META[status]
}
